// NATIVE
import * as React from 'react';

// INSTALLED
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';

import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import DeleteIcon from '@mui/icons-material/Delete';
import Menu, { MenuProps } from '@mui/material/Menu';
import { styled, alpha } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import TableContainer from '@mui/material/TableContainer';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Card, Chip, Pagination, TablePagination } from '@mui/material';

// CUSTOM
import { SnackAlert } from '../SnackAlert';
import { paddingCardsTables, timeToClosedToast } from '../../commons';
import MultipleSnackbars from '../MultipleSnackbars';
import { showNumberRowsTable } from '../../commons/utils/values';
import { ModalSessionExpired } from '../generics/ModalSessionExpired';
import { closeSessionGlobal, hasPemissions } from '../../commons/utils';
import { EditDialogTipoNovedad } from '../modals/tipoNovedades/EditDialogTipoNovedad';
import {
  getTipoNovedadByPagination,
  getTipoNovedadStore,
  storeActiveTipoNovedad,
  storeDesActiveTipoNovedad,
  storeDetailTipoNovedad,
} from '../../store/tipo_novedad';
import { DetalleDialogTipoNovedad } from '../modals/tipoNovedades/DetalleDialogTipoNovedad';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.secondary.contrastText,
    fontSize: 15,
    fontWeight: 700,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: 500,
    color: '#2e263de6',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

/**
 * TableTipoNovedad component renders a table displaying a list of "Tipo Novedades" with pagination and various actions.
 *
 * @component
 *
 * @example
 * return (
 *   <TableTipoNovedad />
 * )
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @description
 * This component fetches and displays a list of "Tipo Novedades" from the Redux store. It provides functionalities to edit, deactivate, and restore items in the list based on user permissions. The component also handles pagination and displays alerts for various actions.
 *
 * @requires
 * - useDispatch from 'react-redux'
 * - useSelector from 'react-redux'
 * - React from 'react'
 * - styled from '@mui/material/styles'
 * - Menu from '@mui/material/Menu'
 * - MenuItem from '@mui/material/MenuItem'
 * - Button from '@mui/material/Button'
 * - Chip from '@mui/material/Chip'
 * - Table from '@mui/material/Table'
 * - TableBody from '@mui/material/TableBody'
 * - TableCell from '@mui/material/TableCell'
 * - TableContainer from '@mui/material/TableContainer'
 * - TableHead from '@mui/material/TableHead'
 * - TableRow from '@mui/material/TableRow'
 * - Paper from '@mui/material/Paper'
 * - Card from '@mui/material/Card'
 * - TablePagination from '@mui/material/TablePagination'
 * - Box from '@mui/material/Box'
 * - Pagination from '@mui/material/Pagination'
 * - KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
 * - EditIcon from '@mui/icons-material/Edit'
 * - DeleteIcon from '@mui/icons-material/Delete'
 * - VisibilityIcon from '@mui/icons-material/Visibility'
 * - MultipleSnackbars from 'path/to/MultipleSnackbars'
 * - SnackAlert from 'path/to/SnackAlert'
 * - ModalSessionExpired from 'path/to/ModalSessionExpired'
 * - EditDialogTipoNovedad from 'path/to/EditDialogTipoNovedad'
 *
 * @state {Array} dataTableList - The list of "Tipo Novedades" to be displayed in the table.
 * @state {Object} pagination - The pagination details for the table.
 * @state {number} page - The current page number for the table.
 * @state {number} rowsPerPage - The number of rows per page for the table.
 * @state {number} pagePagination - The current page number for the pagination component.
 * @state {number} itemCountPagination - The total number of pages for the pagination component.
 * @state {number} timeAlert - The duration for which alerts are displayed.
 * @state {Array} messagesAlerts - The list of alert messages to be displayed.
 * @state {Array} msgErrors - The list of error messages.
 * @state {HTMLElement|null} openElem - The currently open element for the menu.
 * @state {HTMLElement|null} anchorEl - The anchor element for the menu.
 * @state {string} message_alert_snack - The message to be displayed in the snack alert.
 * @state {string} type_alert_snack - The type of the snack alert (e.g., success, error).
 * @state {boolean} open_cargo - The state to control the cargo modal.
 * @state {boolean} modalDetalleTurno - The state to control the detalle turno modal.
 * @state {boolean} modalEditarParameter - The state to control the editar parameter modal.
 * @state {boolean} canEdit - The state to control the edit permission.
 * @state {boolean} canDelete - The state to control the delete permission.
 * @state {boolean} canRestore - The state to control the restore permission.
 * @state {boolean} modalSessionExpired - The state to control the session expired modal.
 * @state {boolean} alert_snack - The state to control the snack alert visibility.
 * @state {Object} msgSnack - The message and status for the snack alert.
 *
 * @function getDataParameters - Fetches the data parameters required for creating "Tipo Novedades".
 * @function handleClick - Handles the click event for opening the menu.
 * @function handleClose - Handles the close event for closing the menu.
 * @function openDialogEditarParameter - Opens the dialog for editing a "Tipo Novedad".
 * @function desactiveTipoNovedad - Deactivates a "Tipo Novedad".
 * @function restoreTipoNovedad - Restores a "Tipo Novedad".
 * @function handleCloseModalEditarTipoNovedad - Closes the editar parameter modal.
 * @function handleChangeRowsPerPage - Handles the change event for rows per page in the table.
 * @function handleChangePage - Handles the change event for page number in the table.
 * @function changePaginationPage - Handles the change event for pagination component.
 * @function validateLsPermissions - Validates the local storage permissions.
 * @function validateResponse - Validates the response from the server.
 * @function checkEdit - Checks if the user has edit permissions.
 * @function checkCanDelete - Checks if the user has delete permissions.
 * @function checkRestore - Checks if the user has restore permissions.
 */
export const TableTipoNovedad = () => {
  // DISPATCH
  const dispatch = useDispatch();

  // REDUX
  const { tiponovedades_list, tiponovedad }: any = useSelector((state: any) => state.tiponovedades);

  // USE STATE
  const [dataTableList, setDataTable] = React.useState([]);
  const [pagination, setPagination] = React.useState<any>({
    page: 1,
    take: 1,
    itemCount: 1,
    pageCount: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(showNumberRowsTable);
  const [pagePagination, setPagePagination] = React.useState(1);
  const [itemCountPagination, setItemCountPagination] = React.useState(1);

  const [timeAlert, setTimeAlert] = React.useState(timeToClosedToast);
  const [messagesAlerts, setMessagesAlerts] = React.useState<any>([]);

  const [msgErrors, setMsgErrors] = React.useState([]);

  const [openElem, setOpenElem] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [message_alert_snack, setMessageAlertSnack] = React.useState('');
  const [type_alert_snack, setTypeAlertSnack] = React.useState('success');

  const [open_cargo, setOpenCargo] = React.useState(false);
  const [modalDetalleTurno, setModalDetalleTurno] = React.useState(false);
  const [modalEditarParameter, setModalEditarTipoNovedad] = React.useState(false);
  const [modalDetalleNovedad, setModalDetalleTipoNovedad] = React.useState(false);

  const [canEdit, setCanEdit] = React.useState<boolean>(false);
  const [canDelete, setCanDelete] = React.useState<boolean>(false);
  const [canRestore, setCanRestore] = React.useState<boolean>(false);

  const [modalSessionExpired, setModalSessionExpired] = React.useState<boolean>(false);

  const [alert_snack, setAlertSnack] = React.useState(false);
  const [msgSnack, setMsgSnack] = React.useState({
    msg: '',
    status: 'success',
  });

  // USE EFFECT
  React.useEffect(() => {
    if (tiponovedades_list.data && tiponovedades_list.data.length > 0) {
      setDataTable(tiponovedades_list.data);
      setPagination({
        page: tiponovedades_list.meta.page,
        take: tiponovedades_list.meta.take,
        itemCount: tiponovedades_list.meta.itemCount,
        pageCount: tiponovedades_list.meta.pageCount,
        hasPreviousPage: tiponovedades_list.meta.hasPreviousPage,
        hasNextPage: tiponovedades_list.meta.hasNextPage,
      });
      setItemCountPagination(tiponovedades_list.meta.pageCount);
    }
  }, [tiponovedades_list]);

  // HERE LOAD THE DATA NECESARY FOR CREATE PARAMETERS
  React.useEffect(() => {
    getDataParameters();
  }, []);

  React.useEffect(() => {
    checkEdit();
  }, []);

  React.useEffect(() => {
    checkCanDelete();
  }, []);

  React.useEffect(() => {
    checkRestore();
  }, []);

  // CONST STYLES
  const StyledMenu = styled((props: MenuProps) => (
    <Menu
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...props}
    />
  ))(({ theme }) => ({
    '& .MuiPaper-root': {
      borderRadius: 6,
      marginTop: theme.spacing(1),
      minWidth: 180,
      color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
      boxShadow:
        'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
      '& .MuiMenu-list': {
        padding: '7px 0',
      },
      '& .MuiMenuItem-root': {
        '& .MuiSvgIcon-root': {
          fontSize: 18,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        '&:active': {
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
        },
      },
    },
  }));

  // CONST STYLES
  const Div = styled('div')(({ theme }) => ({
    ...theme.typography.button,
    backgroundColor: 'wheat',
    padding: theme.spacing(8),
    textAlign: 'center',
  }));

  // CONST VALUE
  const open = Boolean(anchorEl);

  // CONST FUNCTIONS
  const getDataParameters = async () => {
    // modulo.tiponovedades                             nombre modulo     sub modulo    permission
    const havePermissions = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'view',
      'modulo.tiponovedades'
    );

    if (!havePermissions) {
      await validateLsPermissions('No tienes pemisos para (Listar) tipo novedades.');

      return false;
    }

    const response = await dispatch(getTipoNovedadStore());

    await validateResponse(response, ' (Listar Tipo Novedades) ');
  };

  const handleClick = (elem: any) => (event: any) => {
    setAnchorEl(event.currentTarget);
    setOpenElem(elem);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenElem(null);
  };

  const openDialogDetalle = async (tipoNovedadId: string) => {
    setAnchorEl(null);
    setOpenElem(null);

    const havePermissions = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'view',
      'modulo.tiponovedades'
    );

    if (!havePermissions) {
      const errors: any = [];
      errors.push({
        type: 'error',
        text: 'No tienes pemisos para (ver) tipo de novedad.',
      });

      await setMessagesAlerts(errors);

      return false;
    }

    await dispatch(storeDetailTipoNovedad(tipoNovedadId));
    setModalDetalleTipoNovedad(true);
  };

  const openDialogEditarParameter = async (tipoNovedadId: string) => {
    setAnchorEl(null);
    setOpenElem(null);

    const havePermissions = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'edit',
      'modulo.tiponovedades'
    );

    if (!havePermissions) {
      const errors: any = [];
      errors.push({
        type: 'error',
        text: 'No tienes pemisos para (restaurar) tipo de novedad.',
      });

      await setMessagesAlerts(errors);

      return false;
    }

    await dispatch(storeDetailTipoNovedad(tipoNovedadId));
    setModalEditarTipoNovedad(true);
  };

  const desactiveTipoNovedad = async (tipoNovedadId: string) => {
    setAnchorEl(null);
    setOpenElem(null);

    const havePermissions = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'delete',
      'modulo.tiponovedades'
    );

    if (!havePermissions) {
      await validateLsPermissions('No tienes pemisos para (Desactivar) tipo novedad.');

      return false;
    }

    const response = await dispatch(storeDesActiveTipoNovedad(tipoNovedadId));

    await validateResponse(response, ' (Desactivar Tipo Novedades) ');
  };

  const restoreTipoNovedad = async (tipoNovedadId: string) => {
    setAnchorEl(null);
    setOpenElem(null);

    const havePermissions = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'restore',
      'modulo.tiponovedades'
    );

    if (!havePermissions) {
      await validateLsPermissions('No tienes pemisos para (Restaurar) tipo novedad.');

      return false;
    }

    const response = await dispatch(storeActiveTipoNovedad(tipoNovedadId));

    await validateResponse(response, ' (Restaurar Tipo Novedad) ');
  };

  const handleCloseModalEditarTipoNovedad = () => {
    setModalEditarTipoNovedad(false);
  };

  const handleCloseModalDetalleTipoNovedad = () => {
    setModalDetalleTipoNovedad(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);

    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    setPagePagination(value);

    const response = await dispatch(getTipoNovedadByPagination(value));

    if ((!response.ok && response.statusCode == 403) || (response.code && response.code == 403)) {
      setAlertSnack(true);

      await setMessageAlertSnack('No tienes permisos para ( listar ) clientes.');

      setTypeAlertSnack('error');

      return false;
    }
  };

  const validateLsPermissions = async (message: string) => {
    const errors: any = {
      msg: message,
      status: 'error',
    };

    await setMsgSnack(errors);
    await setAlertSnack(true);
  };

  const validateResponse = async (response: any, textActionConcat: string) => {
    if (response.code == 401) {
      await localStorage.removeItem('lastSelectedQypht');
      await closeSessionGlobal();
      setModalSessionExpired(true);
      return false;
    }

    // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION
    if (!response.ok && response.code == 403) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO
    if (!response.ok && response.code == 400) {
      const errors: any = [];

      response.errors.forEach((element: any) => {
        errors.push({
          type: 'error',
          text: element,
        });
      });

      setMessagesAlerts(errors);
      return false;
    }

    return true;
  };

  const checkEdit = async () => {
    const hasPermissionsEdit = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'edit',
      'modulo.tiponovedades'
    );

    if (hasPermissionsEdit) {
      setCanEdit(true);
    }
  };

  const checkCanDelete = async () => {
    const havePermissions = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'delete',
      'modulo.tiponovedades'
    );

    if (havePermissions) {
      setCanDelete(true);
    }
  };

  const checkRestore = async () => {
    const havePermissions = await hasPemissions(
      'modulo.thumano',
      'modulo.novedades',
      'restore',
      'modulo.tiponovedades'
    );

    if (havePermissions) {
      setCanRestore(true);
    }
  };

  return (
    <>
      <ModalSessionExpired open={modalSessionExpired} />

      <MultipleSnackbars
        messagesList={messagesAlerts}
        position={'bottom-left'}
        autoClose={timeAlert}
      />

      <SnackAlert
        open={alert_snack}
        onclick={setAlertSnack}
        text={msgSnack.msg}
        severity={msgSnack.status}
      />

      <DetalleDialogTipoNovedad
        open={modalDetalleNovedad}
        handleCloseModalDetalleTipoNovedad={handleCloseModalDetalleTipoNovedad}
      />
      <EditDialogTipoNovedad
        open={modalEditarParameter}
        handleCloseModalEditarTipoNovedad={handleCloseModalEditarTipoNovedad}
      />

      {dataTableList.length > 0 ? (
        <>
          <Card
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 3,
              borderRadius: 2,
              boxShadow: 3,
              bgcolor: 'background.paper',
              marginTop: paddingCardsTables,
            }}
          >
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>#</StyledTableCell>
                    <StyledTableCell>Nombre</StyledTableCell>
                    <StyledTableCell>Descripcion</StyledTableCell>
                    <StyledTableCell>Estado</StyledTableCell>
                    <StyledTableCell>Acciones</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataTableList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((tiponovedad: any, index: any) => (
                      <StyledTableRow key={index + 1}>
                        <StyledTableCell component="th" scope="row">
                          {index + 1}
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="row">
                          {tiponovedad.name}
                        </StyledTableCell>
                        <StyledTableCell>{tiponovedad.description}</StyledTableCell>

                        <StyledTableCell>
                          {tiponovedad.status ? (
                            <Chip
                              size="small"
                              label="Activo"
                              sx={{ color: '#5DCC0A', background: '#E4F6D6', fontWeight: 700 }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Inactivo"
                              sx={{ color: '#FF4C51', background: '#FFE2E3', fontWeight: 700 }}
                            />
                          )}
                        </StyledTableCell>

                        <StyledTableCell>
                          {
                            <div>
                              <Button
                                id={`demo-customized-button-${index + 1}`}
                                aria-controls={
                                  open ? `demo-customized-menu-${index + 1}` : undefined
                                }
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                variant="text"
                                disableElevation
                                onClick={handleClick(tiponovedad)}
                                endIcon={<KeyboardArrowDownIcon />}
                              >
                                Acciones
                              </Button>
                              <StyledMenu
                                id={`demo-customized-menu-${index + 1}`}
                                MenuListProps={{
                                  'aria-labelledby': `demo-customized-button-${index + 1}`,
                                }}
                                anchorEl={anchorEl}
                                open={openElem === tiponovedad}
                                onClose={handleClose}
                              >
                                {tiponovedad.status ? (
                                  <div>
                                    <MenuItem
                                      onClick={() => openDialogDetalle(tiponovedad.id)}
                                      disableRipple
                                    >
                                      <VisibilityIcon />
                                      Detalle
                                    </MenuItem>

                                    <Divider sx={{ my: 0.5 }} />
                                    {canEdit && (
                                      <>
                                        <MenuItem
                                          onClick={() => openDialogEditarParameter(tiponovedad.id)}
                                          disableRipple
                                        >
                                          <EditIcon />
                                          Editar
                                        </MenuItem>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  ''
                                )}

                                {tiponovedad.status ? (
                                  <div>
                                    {canDelete && (
                                      <>
                                        <Divider sx={{ my: 0.5 }} />
                                        <MenuItem
                                          onClick={() => desactiveTipoNovedad(tiponovedad.id)}
                                          disableRipple
                                        >
                                          <DeleteIcon />
                                          Desactivar
                                        </MenuItem>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    {canRestore && (
                                      <>
                                        <Divider sx={{ my: 0.5 }} />
                                        <MenuItem
                                          onClick={() => restoreTipoNovedad(tiponovedad.id)}
                                          disableRipple
                                        >
                                          <DeleteIcon />
                                          Activar
                                        </MenuItem>
                                      </>
                                    )}
                                  </div>
                                )}
                              </StyledMenu>
                            </div>
                          }
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            labelRowsPerPage={'Filas por pagina'}
            labelDisplayedRows={({ from, to, count }) => ` ${from}-${to} de  ${count} `}
            component="div"
            count={pagination.itemCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 2,
              marginBottom: 2,
            }}
          >
            <Pagination
              sx={{ justifyContent: 'center' }}
              color="primary"
              count={itemCountPagination}
              page={pagePagination}
              onChange={changePaginationPage}
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      ) : (
        <Div>{'No hay tipo novedades disponibles...'}</Div>
      )}
    </>
  );
};
